import { Flag, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { SimulationDataSource, SimulationPerMachine } from "@/types/simulations.types";

interface SimulationAssumptionsProps {
  dataSources: SimulationDataSource[];
  perMachine?: SimulationPerMachine[];
}

// Premissas atribuídas por máquina, com etiqueta medido/premissa.
// Nenhum número órfão — toda premissa nomeia máquina + parâmetro.
export function SimulationAssumptions({ dataSources, perMachine }: SimulationAssumptionsProps) {
  if (!dataSources.length) return null;

  const nameById = new Map((perMachine ?? []).map((p) => [p.machineId, p.name]));
  const groups = new Map<string, SimulationDataSource[]>();
  for (const source of dataSources) {
    const key = source.machineId ?? "—";
    const list = groups.get(key) ?? [];
    list.push(source);
    groups.set(key, list);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Premissas usadas</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          O que a simulação assumiu, por máquina. Leia junto com a confiança.
        </p>
        {[...groups.entries()].map(([machineId, items]) => (
          <div key={machineId} className="space-y-1.5">
            <div className="text-sm font-medium">
              {nameById.get(machineId) ?? machineId.slice(0, 8)}
            </div>
            <ul className="space-y-1">
              {items.map((source, i) => {
                const measured = source.origin === "measured";
                return (
                  <li key={i} className="flex items-center justify-between gap-2 text-sm">
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      {measured ? (
                        <Check className="h-3.5 w-3.5 text-status-running" />
                      ) : (
                        <Flag className="h-3.5 w-3.5 text-warning" />
                      )}
                      {source.parameterName ?? source.component}
                    </span>
                    <span className="flex items-center gap-2">
                      {source.defaulted ? (
                        <span className="text-xs text-muted-foreground">sem dado</span>
                      ) : null}
                      <span
                        className={
                          measured
                            ? "rounded bg-status-running/15 px-1.5 py-0.5 text-xs text-status-running"
                            : "rounded bg-warning/15 px-1.5 py-0.5 text-xs text-warning"
                        }
                      >
                        {measured ? "medido" : "premissa"}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
